/**
 * A job card's Outlook diary entry follows its override dates.
 *
 * - Job card 1 is the workspace's installation booking: overriding it moves the booking and its
 *   Outlook event (or creates them when the job has none).
 * - Later job cards (return visits, crew splits) each own an Outlook event: setting the override
 *   creates it, changing it moves it, clearing it deletes it.
 * - Times: a card is an all-day event across its dates unless it has an end time, when it runs
 *   from its start time to its end time. An all-day card's start time goes at the front of the
 *   Outlook subject ("08:00 Installation: …") and stays on the card; Outlook can't hold it.
 */
import type { Client } from '@microsoft/microsoft-graph-client';
import { directSupabase } from '../../lib/supabaseClient';
import { escapeHtml } from '../../modules/docs/utils/richTextDescription';
import { getGraphClient, getGraphClientSilent } from '../../utils/graphService';
import { inclusiveEventEnd, londonWallClockToIso, outlookBookingDates, type OutlookEventTimes } from '../../utils/outlookEventDates';
import type { Project } from '../../types/project';
import { shiftDayKey, toInstallDateOnly, toInstallDateTimeLocal, toInstallTimeOnly } from './jobCardInstallDate';
import type { JobCardRecord } from './jobCard.types';

const LONDON = 'Europe/London';
/** Finish JARVIS used to give cards without an end time; events booked that way still count as the card's. */
const LEGACY_DAY_END = '17:00';
/** All-day events remind the evening before (18:00), not Outlook's default of 06:00 the day before. */
const ALL_DAY_REMINDER_MINUTES = 6 * 60;

export type DiaryEventTimes = {
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  isAllDay: boolean;
};

/**
 * Outlook times for an override: all day across its dates, unless it has an end time — then it
 * runs from the start time to the end time on the last day.
 */
export function diaryEventTimes(startStamp: string | null | undefined, endDay?: string | null): DiaryEventTimes | null {
  const startDay = toInstallDateOnly(startStamp);
  if (!startDay) return null;
  const requestedEnd = toInstallDateOnly(endDay);
  const lastDay = requestedEnd && requestedEnd > startDay ? requestedEnd : startDay;
  const time = toInstallTimeOnly(startStamp);
  const endTime = toInstallTimeOnly(endDay);
  if (!time || !endTime || (lastDay === startDay && endTime <= time)) {
    return {
      start: { dateTime: `${startDay}T00:00:00`, timeZone: LONDON },
      // All-day events end at midnight after their last day
      end: { dateTime: `${shiftDayKey(lastDay, 1)}T00:00:00`, timeZone: LONDON },
      isAllDay: true,
    };
  }
  return {
    start: { dateTime: `${startDay}T${time}:00`, timeZone: LONDON },
    end: { dateTime: `${lastDay}T${endTime}:00`, timeZone: LONDON },
    isAllDay: false,
  };
}

/** Busy either way, so the diary shows the crew as booked; all-day events remind the evening before. */
export function diaryEventSettings(times: Pick<DiaryEventTimes, 'isAllDay'>) {
  return {
    showAs: 'busy',
    ...(times.isAllDay ? { isReminderOn: true, reminderMinutesBeforeStart: ALL_DAY_REMINDER_MINUTES } : {}),
  };
}

const LEADING_TIME = /^\d{2}:\d{2}\s+/;

/** Subject with the start time at the front (replacing any earlier one), or without one. */
export function withTimePrefix(subject: string, time: string | null | undefined): string {
  const bare = String(subject || '').replace(LEADING_TIME, '');
  return time ? `${time} ${bare}` : bare;
}

/** Graph gives UTC times without a zone suffix unless asked otherwise. */
function zonedStamp(value: string, timeZone?: string): string {
  const raw = String(value || '').trim();
  if (!raw || /(Z|[+-]\d{2}:?\d{2})$/.test(raw)) return raw;
  return /^(UTC|Etc\/UTC)$/i.test(String(timeZone || '')) ? `${raw.replace(/\.\d+$/, '')}Z` : raw.replace(/\.\d+$/, '');
}

/**
 * A card override read from its Outlook event: start stamp, last day, and `end` (the last day
 * with its finish time for a timed event).
 */
export function overrideFromOutlook(outlook: OutlookEventTimes): { start: string; endDay: string; end: string } | null {
  if (!outlook?.start?.dateTime || !outlook?.end?.dateTime) return null;
  if (outlook.isAllDay) {
    const start = outlook.start.dateTime.slice(0, 10);
    const lastDay = inclusiveEventEnd(outlook).slice(0, 10);
    const endDay = lastDay < start ? start : lastDay;
    return { start, endDay, end: endDay };
  }
  const start = toInstallDateTimeLocal(zonedStamp(outlook.start.dateTime, outlook.start.timeZone));
  const endStamp = toInstallDateTimeLocal(zonedStamp(outlook.end.dateTime, outlook.end.timeZone));
  const lastDay = toInstallDateOnly(endStamp);
  if (!start || !endStamp || !lastDay) return null;
  const startDay = start.slice(0, 10);
  if (lastDay < startDay) return { start, endDay: startDay, end: startDay };
  return { start, endDay: lastDay, end: endStamp };
}

/** Whether a card's saved override already matches what Outlook has. */
export function overrideMatches(
  saved: { start: string | null; endDay: string | null; end?: string | null },
  outlook: { start: string; endDay: string; end?: string },
): boolean {
  const savedEnd = saved.endDay || toInstallDateOnly(saved.start);
  const booked = diaryEventTimes(saved.start, saved.end || savedEnd);
  if (!toInstallTimeOnly(outlook.start)) {
    // All day in Outlook: the card's start time (if any) lives on the card, so compare days
    return (!booked || booked.isAllDay)
      && toInstallDateOnly(saved.start) === outlook.start
      && (savedEnd || null) === outlook.endDay;
  }
  if ((saved.start || null) !== outlook.start || (savedEnd || null) !== outlook.endDay) return false;
  if (outlook.end === undefined || !booked) return true;
  const outlookEnd = toInstallTimeOnly(outlook.end) || '00:00';
  if (!booked.isAllDay) return booked.end.dateTime.slice(11, 16) === outlookEnd;
  // A card with no end time, still on an event booked to the old 17:00 finish (re-saving the card makes it all day)
  return outlookEnd === LEGACY_DAY_END;
}

/**
 * A multi-day timed event made by hand in Outlook, reshaped the way JARVIS books it: all day
 * across its days with its start time at the front of the subject. Null when it needs nothing
 * (already all day, or a single-day appointment with its own times).
 */
export function allDayVersionOf(outlook: OutlookEventTimes, subject: string) {
  if (outlook.isAllDay) return null;
  const read = overrideFromOutlook(outlook);
  if (!read || read.endDay <= read.start.slice(0, 10)) return null;
  const times = diaryEventTimes(read.start, read.endDay);
  if (!times) return null;
  return {
    subject: withTimePrefix(subject, toInstallTimeOnly(read.start)),
    ...times,
    ...diaryEventSettings(times),
  };
}

/**
 * An all-day Outlook event moved to new dates: keep the card's start time (Outlook has none),
 * unless the card had an end time, which an all-day event replaces.
 */
export function keepCardStartTime(
  saved: { start: string | null; end?: string | null },
  override: { start: string; endDay: string; end: string },
): { start: string; endDay: string; end: string } {
  const time = toInstallTimeOnly(saved.start);
  if (toInstallTimeOnly(override.start) || !time || toInstallTimeOnly(saved.end)) return override;
  return { start: `${override.start}T${time}`, endDay: override.endDay, end: override.endDay };
}

export function isGraphNotFound(err: unknown): boolean {
  const e = err as { statusCode?: number; code?: string } | null;
  return e?.statusCode === 404 || e?.code === 'ErrorItemNotFound';
}

/**
 * Outlook subject: `<label>: <customer>`, falling back to the project name when there is no
 * customer, with the start time in front for an all-day event.
 */
export function diarySubject(
  label: string,
  customerName?: string | null,
  projectName?: string | null,
  startTime?: string | null,
): string {
  const who = String(customerName || '').trim() || String(projectName || '').trim() || 'Untitled Project';
  return withTimePrefix(`${label}: ${who}`, startTime);
}

/** Outlook body: customer, project and the site address as a Google Maps link. */
export function diaryBody(input: {
  customerName?: string | null;
  projectName?: string | null;
  location?: string | null;
}): { contentType: 'HTML'; content: string } {
  const rows: string[] = [];
  const customer = String(input.customerName || '').trim();
  const project = String(input.projectName || '').trim();
  const address = String(input.location || '').trim();
  if (customer) rows.push(`<b>Customer:</b> ${escapeHtml(customer)}`);
  if (project) rows.push(`<b>Project:</b> ${escapeHtml(project)}`);
  if (address) {
    const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    rows.push(`<b>Address:</b> <a href="${escapeHtml(maps)}">${escapeHtml(address)}</a>`);
  }
  return { contentType: 'HTML', content: rows.join('<br>') };
}

type DiaryInput = { customerName?: string | null; project: Pick<Project, 'project_name'>; location?: string | null };

/** Everything sent to Outlook for a card's dates: subject, body, location, times and settings. */
function diaryEventBody(label: string, times: DiaryEventTimes, input: DiaryInput & { startStamp: string | null }) {
  const startTime = times.isAllDay ? toInstallTimeOnly(input.startStamp) : null;
  return { ...diaryDetails(label, input, startTime), ...times, ...diaryEventSettings(times) };
}

function diaryDetails(label: string, input: DiaryInput, startTime?: string | null) {
  const address = String(input.location || '').trim();
  return {
    subject: diarySubject(label, input.customerName, input.project.project_name, startTime),
    body: diaryBody({ customerName: input.customerName, projectName: input.project.project_name, location: address }),
    ...(address ? { location: { displayName: address } } : {}),
  };
}

/** Create or move an Outlook event; recreates it if it has been deleted in Outlook. Returns its id. */
async function upsertEvent(
  client: Client,
  eventId: string | null,
  body: Record<string, unknown>,
): Promise<string> {
  if (eventId) {
    try {
      await client.api(`/me/events/${eventId}`).patch(body);
      return eventId;
    } catch (err) {
      if (!isGraphNotFound(err)) throw err;
    }
  }
  const created = await client.api('/me/events').post(body);
  return String(created.id);
}

type BookingRow = {
  id: string;
  calendar_event_id?: string | null;
  event_date?: string | null;
  end_date?: string | null;
};

/**
 * Job card 1: move the workspace's live booking (and its Outlook event) to the override dates,
 * creating both when the job has none yet.
 */
export async function moveBookingToOverride(input: {
  project: Project;
  booking: BookingRow | null;
  eventType: 'installationDateConfirmation' | 'deliveryDateConfirmation';
  startStamp: string;
  /** Last day, as a stamp when it carries the finish time. */
  endDay: string | null;
  /** Site address for the Outlook event's location. */
  location?: string | null;
  /** Customer name for the Outlook subject and body. */
  customerName?: string | null;
  userId?: string | null;
}): Promise<void> {
  const times = diaryEventTimes(input.startStamp, input.endDay);
  if (!times) return;
  const client = await getGraphClient();
  const label = input.eventType === 'deliveryDateConfirmation' ? 'Delivery' : 'Installation';
  const body = diaryEventBody(label, times, input);
  const subject = body.subject;
  // Existing events are renamed too, so older bookings pick up the customer-name convention
  const eventId = await upsertEvent(client, input.booking?.calendar_event_id || null, body);
  // Read back in UTC so the booking stores the same instants Outlook has; an all-day booking keeps the card's start time
  const saved: OutlookEventTimes = await client.api(`/me/events/${eventId}`).select('start,end,isAllDay').get();
  const startDay = toInstallDateOnly(input.startStamp);
  const startTime = toInstallTimeOnly(input.startStamp);
  const dates = outlookBookingDates(saved, startDay && startTime ? londonWallClockToIso(startDay, startTime) : null);
  if (!dates) return;
  const stamp = { updated_at: new Date().toISOString(), ...(input.userId ? { updated_by: input.userId } : {}) };
  if (input.booking) {
    const { error } = await directSupabase
      .from('project_events')
      .update({ ...dates, calendar_event_id: eventId, status: 'scheduled', ...stamp })
      .eq('id', input.booking.id);
    if (error) throw new Error(error.message);
    return;
  }
  const { error } = await directSupabase.from('project_events').insert({
    project_id: input.project.id,
    event_type: input.eventType,
    status: 'scheduled',
    title: subject,
    is_automated: false,
    calendar_event_id: eventId,
    ...dates,
    ...(input.userId ? { created_by: input.userId } : {}),
    ...stamp,
  });
  if (error) throw new Error(error.message);
}

/**
 * Later job cards: create, move or (when the override is cleared) delete the card's own
 * Outlook event. Returns the card's event id afterwards.
 */
export async function saveCardDiaryEvent(input: {
  card: Pick<JobCardRecord, 'title' | 'calendar_event_id'>;
  project: Project;
  startStamp: string | null;
  /** Last day, as a stamp when it carries the finish time. */
  endDay: string | null;
  /** Site address for the Outlook event's location. */
  location?: string | null;
  /** Customer name for the Outlook subject and body. */
  customerName?: string | null;
}): Promise<string | null> {
  const client = await getGraphClient();
  const eventId = input.card.calendar_event_id;
  const times = diaryEventTimes(input.startStamp, input.endDay);
  if (!times) {
    if (eventId) await deleteCardDiaryEvent(eventId, client);
    return null;
  }
  return upsertEvent(client, eventId, diaryEventBody(input.card.title || 'Job card', times, input));
}

/** Remove a card's Outlook event; already gone counts as done. */
export async function deleteCardDiaryEvent(eventId: string, client?: Client): Promise<void> {
  const graph = client || await getGraphClient();
  try {
    await graph.api(`/me/events/${eventId}`).delete();
  } catch (err) {
    if (!isGraphNotFound(err)) throw err;
  }
}

/**
 * Reads a later card's Outlook event. `missing` when it has been deleted in Outlook;
 * `override` when Outlook's dates differ from the card's. Skipped (both empty) for anyone
 * not signed in to Microsoft.
 */
export async function checkCardDiaryEvent(input: {
  eventId: string;
  saved: { start: string | null; endDay: string | null; end?: string | null };
}): Promise<{ missing: boolean; override: { start: string; endDay: string; end: string } | null }> {
  const client = await getGraphClientSilent();
  if (!client) return { missing: false, override: null };
  try {
    const outlook: OutlookEventTimes = await client
      .api(`/me/events/${input.eventId}`)
      .select('start,end,isAllDay')
      .get();
    const override = overrideFromOutlook(outlook);
    if (!override || overrideMatches(input.saved, override)) return { missing: false, override: null };
    return { missing: false, override: keepCardStartTime(input.saved, override) };
  } catch (err) {
    return { missing: isGraphNotFound(err), override: null };
  }
}
